/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core';
import {
    CreateDeliveryListMutationVariables,
    UnitType,
    refetchRossumUploadsByWarehouseIdQuery,
    refetchRossumUploadsCountByWarehouseIdQuery,
    useCompleteRossumUploadMutation,
    useCreateDeliveryListMutation,
    useCreateOrReplaceDeliveryOptionsMutation,
    useRossumProcessedInvoiceByRossumUploadIdQuery,
} from '../../../../generated/graphql';
import { setUrlParams } from '../../../../utils/helpers/urlParams';
import { useWarehousesScope } from '../../../warehouse/providers/WarehouseScopeProvider';
import { FullScreenDialog, FullScreenLoader } from '../../../../ui-kit';
import { routePaths } from '../../../../routePaths';
import DeliveryListForm from './DeliveryListForm';
import { DeliveryListFormValues } from './types';

const VerifyDeliveryListForm = () => {
    const { t } = useTranslation();
    const { currentWarehouseId } = useWarehousesScope();
    const { params: { id: rossumUploadId } } = useRouteMatch<{ id: string }>();
    const history = useHistory();
    const theme = useTheme();

    const [isOpened, setIsOpened] = useState(true);

    const { data: rossumProcessedInvoice, loading } = useRossumProcessedInvoiceByRossumUploadIdQuery({
        variables: {
            rossumUploadId,
            warehouseId: currentWarehouseId as string,
        },
        fetchPolicy: 'network-only'
    });

    const [createOrReplaceDeliveryOptions] = useCreateOrReplaceDeliveryOptionsMutation();
    const [createDeliveryList] = useCreateDeliveryListMutation();
    const [completeRossumUpload] = useCompleteRossumUploadMutation({
        refetchQueries: [
            refetchRossumUploadsByWarehouseIdQuery({ warehouseId: currentWarehouseId as string }),
            refetchRossumUploadsCountByWarehouseIdQuery({ warehouseId: currentWarehouseId as string }),
        ],
    });

    const handleClose = () => {
        setIsOpened(false);

        setTimeout(() => {
            history.push(routePaths.warehouseDeliveryLists);

            const urlParams = setUrlParams(history.location.search, {
                showInProcess: true,
            });
            history.push({ search: urlParams.toString() });
        }, theme.transitions.duration.leavingScreen);
    };

    const onSubmit = async ({ invoiceNumber, supplierId, deliveryDateUtc, pendingListItems }: DeliveryListFormValues) => {
        const deliveryOptions = pendingListItems.map(({ code, quantity, finalAmount, unitOfMeasure, stockCard }) => ({
            supplierId,
            itemCode: code,
            stockCardId: stockCard?.id as string,
            conversionMultiplier: (finalAmount as number) / (quantity as number),
            conversionUnitType: unitOfMeasure as UnitType,
        }));

        const deliveryListItems = pendingListItems.map(({ finalAmount, unitOfMeasure, unitPriceWithoutVat, stockCard }) => ({
            quantity: finalAmount as number,
            unitType: unitOfMeasure as UnitType,
            stockCardId: stockCard?.id as string,
            priceWithoutVat: unitPriceWithoutVat as number,
        }));

        const variables: CreateDeliveryListMutationVariables = {
            data: {
                invoiceNumber,
                supplierId,
                deliveryDateUtc,
                deliveryListItems,
                warehouseId: currentWarehouseId as string,
            },
        };

        await createOrReplaceDeliveryOptions({
            variables: { deliveryOptions },
        });

        await createDeliveryList({ variables });

        if (rossumUploadId) {
            await completeRossumUpload({ variables: { rossumUploadId } });
        }

        handleClose();
    };

    const invoice = rossumProcessedInvoice?.rossumProcessedInvoiceByRossumUploadId;
    if (loading || !invoice) {
        return <FullScreenLoader />;
    }

    const { invoiceNumber, deliveryDate, supplierId, processedListItems } = invoice;
    const initialValues = {
        invoiceNumber,
        supplierId,
        deliveryDateUtc: deliveryDate ? new Date(deliveryDate) : null,
        pendingListItems: processedListItems,
        deliveryListItems: [],
    };

    return (
        <FullScreenDialog isOpened={isOpened}>
            <DeliveryListForm
                dialogName={t('Verify delivery list')}
                verifyingUploadId={rossumUploadId}
                initialValues={initialValues}
                onClose={handleClose}
                onSubmit={onSubmit}
            />
        </FullScreenDialog>
    );
};

export default VerifyDeliveryListForm;
